import React, { useMemo } from 'react'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'

import GreenArrow from '../../stories/assets/icons/up_arrow.svg'
import { BLUE_BASE } from '../../theme/variables/colors'
import { hexToRgba } from '../../utils/functions'
import TodaysDistribution from '../FundApiComponents/TodaysDistribution'
import Popover from '../Popover/Popover'

const getDividerColor = (theme) => hexToRgba(BLUE_BASE, 0.4)

// Update ColumnsContainer to accept an "isSingleDataPoint" prop.
const ColumnsContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSingleDataPoint',
})(({ theme, isSingleDataPoint }) => ({
  width: '100%',
  display: 'grid',
  gridTemplateColumns: isSingleDataPoint ? '1fr' : 'repeat(4, 1fr)',
  rowGap: theme.spacing(isSingleDataPoint ? 7 : 7),
  marginBottom: theme.spacing(isSingleDataPoint ? 7 : 7),
  alignItems: 'stretch',
  '& > div': {
    padding: '0px 16px',
  },
  // Only apply border styles when multiple columns are present.
  '& > div:not(.empty):not(:nth-of-type(4n+1))': {
    borderLeft: isSingleDataPoint
      ? 'none'
      : `1px solid ${getDividerColor(theme)}`,
    paddingLeft: isSingleDataPoint ? 0 : theme.spacing(3),
  },
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: isSingleDataPoint ? '1fr' : 'repeat(2, 1fr)',
    rowGap: theme.spacing(isSingleDataPoint ? 4 : 4),
    marginBottom: theme.spacing(isSingleDataPoint ? 4 : 4),
    '& > div:not(:nth-of-type(4n+1))': {
      borderLeft: 'none',
    },
    '& > div': {
      borderLeft: 'none',
      paddingLeft: 0,
    },
    '& > div:nth-of-type(odd):not(.empty)': {
      borderRight: isSingleDataPoint
        ? 'none'
        : `1px solid ${getDividerColor(theme)}`,
      borderLeft: 'none',
      padding: isSingleDataPoint ? 0 : `0 ${theme.spacing(3)}`,
    },
  },
}))

const DisplayColumn = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: '0px',
  gap: '16px',
}))

const Column = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSingleDataPoint',
})(({ isSingleDataPoint, theme }) => ({
  marginTop: theme.spacing(7),
  [theme.breakpoints.down('md')]: {
    marginTop: theme.spacing(4),
  },
}))

const EmptyColumn = styled(Box)(({ theme }) => ({
  borderLeft: 'none !important',
  borderRight: 'none !important',
  paddingLeft: '0px !important',
  paddingRight: '0px !important',
  [theme.breakpoints.down('sm')]: {
    height: '0 !important',
    margin: '0 !important',
    padding: '0 !important',
  },
}))

const SingleDataPointContainer = styled(Box)(({ theme }) => ({
  marginTop: '4vh',
}))

const SingleDataPointLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
}))

const SingleDataPointValue = styled(Typography)(({ theme }) => ({
  fontSize: '1.3em',
  fontWeight: 500,
  lineHeight: '1.4em',
  paddingLeft: '0 !important',
  borderLeft: 'unset !important',
  [theme.breakpoints.down('md')]: {
    fontSize: '12px',
    lineHeight: '13px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '12px',
    lineHeight: '13px',
  },
}))

const FundChangeUp = styled(Box)(({ theme }) => ({
  color: theme.palette.success.dark,
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  '&:after': {
    content: '""',
    width: '27px',
    height: '27px',
    marginLeft: '10px',
    background: `url(${GreenArrow}) no-repeat center`,
    backgroundSize: '100%',
    filter:
      'invert(37%) sepia(80%) saturate(442%) hue-rotate(105deg) brightness(99%) contrast(86%)',
    right: '-10px',
    top: 0,
  },
  [theme.breakpoints.down('sm')]: {
    justifyContent: 'flex-start',
    '&:after': {
      width: '0px',
      height: '0px',
      marginLeft: '0px',
      display: 'none',
    },
    '&:before': {
      content: '""',
      width: '27px',
      height: '27px',
      marginRight: '10px',
      background: `url(${GreenArrow}) no-repeat center`,
      backgroundSize: '100%',
      filter:
        'invert(37%) sepia(80%) saturate(442%) hue-rotate(105deg) brightness(99%) contrast(86%)',
      marginTop: '-1.7vw',
      left: '-10px',
    },
  },
}))

const FundChangeDown = styled(FundChangeUp)(({ theme }) => ({
  color: theme.palette.error.main,
  '&:after': {
    transform: 'rotate(180deg)',
    filter:
      'invert(51%) sepia(79%) saturate(7456%) hue-rotate(345deg) brightness(81%) contrast(108%)',
  },
  [theme.breakpoints.down('sm')]: {
    '&:before': {
      transform: 'rotate(180deg)',
      filter:
        'invert(51%) sepia(79%) saturate(7456%) hue-rotate(345deg) brightness(81%) contrast(108%)',
    },
  },
}))

const DatapointValue = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'length',
})(({ theme, length }) => ({
  textAlign: 'left',
  height: 'fit-content',
  borderLeft: 'none !important',
  borderRight: 'none !important',
  paddingLeft: '0px !important',
  paddingRight: '0px !important',
  fontSize: '2.8em',
  [theme.breakpoints.down('sm')]: {
    display: 'block',
    fontSize: '24px',
  },
  ...(length > 30 && {
    fontSize: '1.3em',
    lineHeight: '1.4em',
    fontWeight: 500,
    [theme.breakpoints.down('xs')]: {
      fontSize: '12px',
      lineHeight: '16px',
      fontWeight: '600',
    },
  }),
  ...(length > 20 &&
    length <= 30 && {
      fontSize: '1.8em',
      lineHeight: '1.1em',
      fontWeight: 500,
      [theme.breakpoints.down('xs')]: {
        fontSize: '16px',
        lineHeight: '19px',
        fontWeight: '600',
      },
    }),
  ...(length > 10 &&
    length <= 20 && {
      fontSize: '2em',
      lineHeight: '1.1em',
      [theme.breakpoints.down('xs')]: {
        fontSize: '18px',
        lineHeight: '21px',
        fontWeight: '600',
      },
    }),
  ...(length > 0 &&
    length <= 10 && {
      fontSize: '2.8em',
      lineHeight: '1.05em',
      [theme.breakpoints.down('xs')]: {
        fontSize: '24px',
        fontWeight: '600',
      },
    }),
}))

const DatapointLabel = styled(Box)(({ theme }) => ({
  fontWeight: 600,
  textAlign: 'left',
  borderLeft: 'none !important',
  borderRight: 'none !important',
  paddingLeft: '0px !important',
  paddingRight: '0px !important',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    fontSize: '12px',
    lineHeight: '20px',
    fontWeight: '500',
    marginTop: '0px',
    display: 'block',
  },
}))

const DisplayDiv = styled(Box)(({ theme }) => ({}))
const DisplayFlexDiv = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
}))
const DisplayFlexPopoverDiv = styled(Box)(({ theme }) => ({
  display: 'flex',
}))
const PopoverFlexDiv = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  marginTop: '4vh',
  [theme.breakpoints.down('sm')]: {
    justifyContent: 'flex-end',
  },
}))
const PopoverSpacing = styled(Box)(({ theme }) => ({
  marginRight: theme.spacing(5),
  [theme.breakpoints.down('sm')]: {
    marginRight: '0px',
  },
}))
const DatapointValueHeight = styled(Box)(({ theme }) => ({}))
const NoBorderDiv = styled(Box)(({ theme }) => ({
  borderLeft: 'none !important',
  borderRight: 'none !important',
  paddingLeft: '0px !important',
  paddingRight: '0px !important',
}))
const TableTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.5em',
  fontWeight: 700,
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.2em',
    marginBottom: theme.spacing(2),
  },
}))
const ColumnHeader = styled(Typography)(({ theme }) => ({
  fontSize: '1.2em',
  fontWeight: 600,
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    fontSize: '1em',
    marginBottom: theme.spacing(1.5),
  },
}))

const popoverStyle = {
  marginTop: '32px',
  marginLeft: '10px',
}

const popoverTextStyle = {
  marginTop: '0px',
}

const FourColumnsDatapoints = React.memo(
  ({ title, columnHeaders, datapoints }) => {
    const isSingleDataPoint = datapoints.length === 1

    // If there are column headers, pass the isSingleDataPoint prop so the headers layout adjusts accordingly.
    const renderedColumnHeaders = useMemo(() => {
      if (!columnHeaders || columnHeaders.length === 0) return null
      return (
        <ColumnsContainer isSingleDataPoint={isSingleDataPoint}>
          {columnHeaders.map((header, index) => (
            <Column
              key={`header-${index}`}
              isSingleDataPoint={isSingleDataPoint}
            >
              <ColumnHeader variant="subtitle1">{header}</ColumnHeader>
            </Column>
          ))}
        </ColumnsContainer>
      )
    }, [columnHeaders, isSingleDataPoint])

    const renderedDatapoints = useMemo(() => {
      return datapoints.map((datapoint, index) => {
        const isEmptyColumn = Object.keys(datapoint).length === 0

        if (isSingleDataPoint) {
          return (
            <SingleDataPointContainer key={index}>
              <SingleDataPointLabel variant="body2">
                {datapoint.label}:
              </SingleDataPointLabel>
              <SingleDataPointValue variant="body2">
                {datapoint.value}
              </SingleDataPointValue>
            </SingleDataPointContainer>
          )
        }

        return (
          <Column
            key={index}
            isSingleDataPoint={isSingleDataPoint}
            className={isEmptyColumn ? 'empty' : ''}
          >
            <DisplayColumn>
              {isEmptyColumn ? null : (
                <>
                  <NoBorderDiv>
                    <DatapointValueHeight>
                      {datapoint.hasFundChangeUp ? (
                        <FundChangeUp>
                          <DatapointValue
                            variant="body2"
                            length={datapoint.value.length}
                          >
                            {datapoint.value}
                          </DatapointValue>
                        </FundChangeUp>
                      ) : datapoint.hasFundChangeDown ? (
                        <FundChangeDown>
                          <DatapointValue
                            variant="body2"
                            length={datapoint.value.length}
                          >
                            {datapoint.value}
                          </DatapointValue>
                        </FundChangeDown>
                      ) : (
                        <DisplayDiv>
                          <DatapointValue
                            variant="body2"
                            length={datapoint.value.length}
                          >
                            {datapoint.value}
                          </DatapointValue>
                        </DisplayDiv>
                      )}
                    </DatapointValueHeight>
                  </NoBorderDiv>
                  <NoBorderDiv>
                    <DisplayFlexPopoverDiv>
                      {datapoint.labelHasAsOfDate ? (
                        <DisplayDiv>
                          <DatapointLabel>{datapoint.label}</DatapointLabel>
                          <DatapointLabel sx={popoverTextStyle}>
                            {datapoint.labelAsOfDate}
                          </DatapointLabel>
                        </DisplayDiv>
                      ) : (
                        <DatapointLabel>{datapoint.label}</DatapointLabel>
                      )}
                      {datapoint.labelHasPopover ? (
                        datapoint.popover.htmlContent ? (
                          datapoint.popover.htmlContent.length === 0 ? (
                            datapoint.popover.text.map((text, idx) => (
                              <Typography
                                key={`popover-text-${idx}`}
                                component="p"
                              >
                                {text}
                              </Typography>
                            ))
                          ) : (
                            <Popover
                              htmlContent={datapoint.popover.htmlContent}
                              position="top"
                              sx={popoverStyle}
                              overviewSection
                              mobileMaxWidth="22px"
                              aria-label={`Popover for ${datapoint.label}`}
                            />
                          )
                        ) : (
                          <Popover
                            content={datapoint.popover.content}
                            header={datapoint.popover.title}
                            sx={popoverStyle}
                            overviewSection
                            mobileMaxWidth="22px"
                            aria-label={`Popover for ${datapoint.label}`}
                          />
                        )
                      ) : (
                        datapoint.hasTodaysDistribution && (
                          <TodaysDistribution
                            todaysDistribution={datapoint.todaysDistribution}
                          />
                        )
                      )}
                    </DisplayFlexPopoverDiv>
                  </NoBorderDiv>
                </>
              )}
            </DisplayColumn>
          </Column>
        )
      })
    }, [datapoints, isSingleDataPoint])

    return (
      <Box>
        {title && <TableTitle variant="h6">{title}</TableTitle>}
        {renderedColumnHeaders}
        <ColumnsContainer isSingleDataPoint={isSingleDataPoint}>
          {renderedDatapoints}
        </ColumnsContainer>
      </Box>
    )
  }
)

FourColumnsDatapoints.displayName = 'FourColumnsDatapoints'

export default FourColumnsDatapoints
